import polyLabel from 'polylabel';
import BoundingBox from './bounding_box';
import Point from './point';
import LineSegment from './line_segment';

class Polygon {
	points = null;

	edges = null;

	boundingBox = null;

	center = null;

	visualCenter = null;

	constructor(points) {
		this.points = points;

		this.edges = [];
		for (let i = 0; i < points.length; i++) {
			const currentPoint = points[i];
			const nextPoint = points[(i + 1) % points.length];

			this.edges.push(LineSegment.fromPoints(currentPoint, nextPoint));
		}

		this.boundingBox = BoundingBox.fromPoints(points);
		this.center = this.getCenter();
		this.visualCenter = this.getVisualCenter();
	}

	static fromCoords(coords) {
		if (coords.length % 2 !== 0) {
			throw new Error(`Invalid Polygon Points: ${coords}`);
		}

		const points = [];
		for (let i = 0; i < coords.length; i += 2) {
			points.push(new Point(coords[i], coords[i + 1]));
		}

		return new Polygon(points);
	}

	static fromRect(rect) {
		const x = rect.x || 0;
		const y = rect.y || 0;
		const width = rect.width || 0;
		const height = rect.height || 0;

		if (width <= 0) {
			throw new Error(`Invalid Rect Width: ${rect}`);
		}

		if (height <= 0) {
			throw new Error(`Invalid Rect Height: ${rect}`);
		}

		return new Polygon([
			new Point(x, y),
			new Point(x + width, y),
			new Point(x + width, y + height),
			new Point(x, y + height),
		]);
	}

	simplify(fix = 10) {
		const points = [];

		this.points.forEach((point) => {
			points.push(new Point(Math.round(point.x / fix), Math.round(point.y / fix)));
		});

		return new Polygon(points);
	}

	containsPoint(point, inclusive = false, checkBoundingBox = true) {
		if (checkBoundingBox && !this.boundingBox.containsPoint(point, inclusive)) {
			return false;
		}

		let i = 0;
		let j = this.points.length - 1;
		let c = false;

		const compare = inclusive ? (a, b) => a >= b : (a, b) => a > b;

		for (; i < this.points.length; j = i++) {
			if (
				compare(this.points[i].y, point.y) !== compare(this.points[j].y, point.y) &&
				point.x <
					((this.points[j].x - this.points[i].x) * (point.y - this.points[i].y)) /
						(this.points[j].y - this.points[i].y) +
						this.points[i].x
			) {
				c = !c;
			}
		}

		return c;
	}

	overlapsWith(polygon, inclusive = false) {
		const intersection = this.boundingBox.getIntersectionBox(polygon.boundingBox, inclusive);

		if (intersection) {
			const aPoints = this.points.filter((point) => intersection.containsPoint(point, inclusive));
			const bPoints = polygon.points.filter((point) => intersection.containsPoint(point, inclusive));

			if (aPoints.length === 0 && bPoints.length === 0) {
				return false;
			}

			for (let i = 0; i < aPoints.length; i++) {
				const point = aPoints[i];
				if (polygon.containsPoint(point, true, false)) {
					return true;
				}
			}

			for (let i = 0; i < bPoints.length; i++) {
				const point = bPoints[i];
				if (this.containsPoint(point, true, false)) {
					return true;
				}
			}
		}

		return false;
	}

	containsPolygon(polygon, inclusive = false) {
		const intersection = this.boundingBox.getIntersectionBox(polygon.boundingBox, inclusive);

		if (intersection) {
			for (let i = 0; i < polygon.points.length; i++) {
				const point = polygon.points[i];

				if (!this.containsPoint(point, true, false)) {
					return false;
				}
			}

			return true;
		}

		return false;
	}

	intersectWith(polygon) {
		const p1 = this.clone();
		const p2 = polygon.clone();

		for (let i = 0; i < p1.edges.length; i++) {
			for (let j = 0; j < p2.edges.length; j++) {
				let intersection = p1.edges[i].intersectsWith(p2.edges[j]);

				if (intersection) {
					intersection = intersection instanceof Array ? intersection : [intersection];

					if (intersection.length > 1) {
						const startPoint = new Point(p1.edges[i].x1, p1.edges[i].y1);

						intersection.sort((a, b) => {
							return a.distanceFrom(startPoint) - b.distanceFrom(startPoint);
						});
					}

					let edgeFixI = null;
					const newEdgesI = [];
					const newPointsI = [];

					for (let k = 0; k < intersection.length; k++) {
						const ip = intersection[k];

						if (
							!(
								(ip.x === p1.edges[i].x1 && ip.y === p1.edges[i].y1) ||
								(ip.x === p1.edges[i].x2 && ip.y === p1.edges[i].y2)
							)
						) {
							newEdgesI.push(new LineSegment(ip.x, ip.y, p1.edges[i].x2, p1.edges[i].y2));

							newPointsI.push(ip);

							if (!edgeFixI) {
								edgeFixI = ip;
							}
						}
					}

					if (edgeFixI) {
						p1.edges[i] = new LineSegment(p1.edges[i].x1, p1.edges[i].y1, edgeFixI.x, edgeFixI.y);

						p1.edges.splice(i + 1, -newEdgesI.length, ...newEdgesI);
						p1.points.splice(i + 1, -newPointsI.length, ...newPointsI);
					}

					if (intersection.length > 1) {
						const startPoint = new Point(p2.edges[j].x1, p2.edges[j].y1);

						intersection.sort((a, b) => {
							return a.distanceFrom(startPoint) - b.distanceFrom(startPoint);
						});
					}

					let edgeFixJ = null;
					const newEdgesJ = [];
					const newPointsJ = [];

					for (let k = 0; k < intersection.length; k++) {
						const ip = intersection[k];

						if (
							!(
								(ip.x === p2.edges[j].x1 && ip.y === p2.edges[j].y1) ||
								(ip.x === p2.edges[j].x2 && ip.y === p2.edges[j].y2)
							)
						) {
							newEdgesJ.push(new LineSegment(ip.x, ip.y, p2.edges[j].x2, p2.edges[j].y2));

							newPointsJ.push(ip);

							if (!edgeFixJ) {
								edgeFixJ = ip;
							}
						}
					}

					if (edgeFixJ) {
						p2.edges[j] = new LineSegment(p2.edges[j].x1, p2.edges[j].y1, edgeFixJ.x, edgeFixJ.y);

						p2.edges.splice(j + 1, -newEdgesJ.length, ...newEdgesJ);
						p2.points.splice(j + 1, -newPointsJ.length, ...newPointsJ);
					}
				}
			}
		}

		const graph = {};
		const points = {};

		p1.points.forEach((point) => {
			points[`${point.x}:${point.y}`] = point;
		});

		p2.points.forEach((point) => {
			points[`${point.x}:${point.y}`] = point;
		});

		p1.edges.forEach((edge) => {
			const pk = `${edge.x1}:${edge.y1}`;

			graph[pk] = graph[pk] || [];

			graph[pk].push(edge);
		});

		p2.edges.forEach((edge) => {
			const pk = `${edge.x1}:${edge.y1}`;

			graph[pk] = graph[pk] || [];

			if (!graph[pk].find((item) => item.equalsTo(edge))) {
				graph[pk].push(edge);
			}
		});

		return {
			graph,
			points,
		};
	}

	mergeWith(polygon, inclusive = false) {
		if (this.overlapsWith(polygon, inclusive)) {
			if (this.containsPolygon(polygon, inclusive)) {
				return this;
			}

			if (polygon.containsPolygon(this)) {
				return polygon;
			}

			const { graph, points } = this.intersectWith(polygon);

			const actualPoints = Object.values(points);
			const minX = Math.min(...actualPoints.map((point) => point.x));
			const minY = Math.min(...actualPoints.map((point) => point.y));
			const minPoint = actualPoints.sort(
				(a, b) => Math.hypot(a.x - minX, a.y - minY) - Math.hypot(b.x - minX, b.y - minY)
			)[0];
			let currentEdge = graph[`${minPoint.x}:${minPoint.y}`][0];
			let nextPoint = points[`${currentEdge.x2}:${currentEdge.y2}`];

			const mergedPoints = [minPoint];

			while (nextPoint !== minPoint) {
				mergedPoints.push(nextPoint);

				const edges = graph[`${nextPoint.x}:${nextPoint.y}`];

				if (edges.length > 1) {
					// eslint-disable-next-line no-loop-func
					const angles = edges.map((edge) => currentEdge.getCounterClockwiseAngleBetween(edge));
					const maxAngle = Math.max(...angles);
					const maxIndex = angles.indexOf(maxAngle);

					currentEdge = edges[maxIndex];
				} else {
					[currentEdge] = edges;
				}

				nextPoint = points[`${currentEdge.x2}:${currentEdge.y2}`];
			}

			return new Polygon(mergedPoints);
		}

		return null;
	}

	clone() {
		return new Polygon(this.points.map((point) => new Point(point.x, point.y)));
	}

	getCenter() {
		let xSum = 0;
		let ySum = 0;

		this.points.forEach((point) => {
			xSum += point.x;
			ySum += point.y;
		});

		return new Point(xSum / this.points.length, ySum / this.points.length);
	}

	getVisualCenter() {
		const points = this.points.map((point) => [point.x, point.y]);

		const [cx, cy] = polyLabel([points]);

		return new Point(cx, cy);
	}
}

export default Polygon;
